/**
 * Highcharts options for the phone charts: a complete options object of its
 * own (the desktop getBaseTimeSeriesOptions is not touched), built for a
 * small touch screen:
 *  - no title, subtitle, credits, export menu, range selector, navigator,
 *    scrollbar or zoom; the HTML legend chips (LegendChips) replace the
 *    chart legend, and the range is chosen above the chart;
 *  - line series, no data grouping, no animation, a shared tooltip that
 *    follows the finger;
 *  - findNearestPointBy 'x' on every series, the achievement markers too:
 *    Highcharts takes over the touch (touch-action: none, so the page cannot
 *    scroll) only when a series searches by y, as scatter series do by
 *    default;
 *  - short limit labels ("Max ≥75°C", 10px).
 *
 * Pure functions (no React, no API calls). Highcharts is imported for its
 * types only: the test stubs of Highcharts lack its helpers.
 */
import type Highcharts from 'highcharts/highstock';
// Registers the %Z date format (the timezone's short name) the tooltips use.
import '../chartOptions';
import type {
  ChartDataResponse,
  ChartLimit,
  DifferentialNow,
  MaturityDescriptor,
  Pour2Limit,
  TempLimit,
} from '../../../services/pours';
import {
  TARGET_OUTSIDE_RANGE_LABEL,
  isTargetOutsideRange,
  maturityMeta,
  resolveStrengthUnit,
  targetDisplayName,
  targetPurpose,
} from '../../../utils/maturityDisplay';
import type { TranslateFn } from '../../../utils/relativeTime';
import { shortLimitLines, temperatureLimitLines, type PhoneLimit } from '../limitLines';
import {
  SERIES_COLORS,
  markerSeries,
  strengthPointFormatter,
  targetLinePriorities,
  targetPlotLines,
  type MetricConfig,
  type OverlayPanel,
  type OverlayTargetLine,
} from '../overlayModel';
import {
  REACHED_MARKER_COLOR,
  achievementPoints,
  declutterTargetLabels,
  extrapolatedZones,
  maturityAxisSoftMax,
  strengthAxisSoftMax,
  testedRangeBand,
  testedTimeSpan,
} from '../strengthChart';

/** The Charts tab's overlay chart height. */
export const PHONE_OVERLAY_HEIGHT = 280;
/** The Location tab's chart height. */
export const PHONE_LOCATION_CHART_HEIGHT = 260;
/** Target labels closer than this (px) are hidden on the phone. */
const PHONE_LABEL_GAP = 20;
/** Room above the highest evaporation limit (kg/m²/h) for its label. */
const EVAPORATION_LABEL_HEADROOM = 0.1;

const AXIS_LABEL_STYLE = { fontSize: '11px' };

// Series colours of the single-location charts, as Location Details.
const TEMP_COLOR = '#79A82A';
const TANK_COLOR = '#ff9800';
const MATURITY_COLOR = '#9c27b0';
const STRENGTH_COLOR = '#1976d2';
const EVAPORATION_COLOR = '#4caf50';

/** One series in the legend chips. */
export interface PhoneLegendItem {
  /** The series id (hidden ids hide the series). */
  id: string;
  label: string;
  color: string;
  /** The series' newest value with its unit ("42.3 °C"), for the legend
   *  list; absent when it has no point. */
  value?: string;
}

/** The newest point's value of a series, formatted for the legend list. */
const newestValueText = (data: ReadonlyArray<[number, number]>, decimals: number, suffix: string): string | undefined => {
  let newest: [number, number] | null = null;
  data.forEach((p) => {
    if (newest === null || p[0] > newest[0]) newest = p;
  });
  if (newest === null) return undefined;
  const value = (newest as [number, number])[1];
  return Number.isFinite(value) ? `${value.toFixed(decimals)}${suffix}` : undefined;
};

/** A phone chart ready to draw. */
export interface PhoneChartBuild {
  options: Highcharts.Options;
  /** The line series, for the legend chips. */
  legend: PhoneLegendItem[];
  /** Some series has a point; otherwise the chart shows its empty state. */
  hasData: boolean;
  /** The newest point (epoch ms), the end of the x-axis window. */
  latest: number | null;
}

const NOTHING_HIDDEN: ReadonlySet<string> = new Set<string>();

/** English defaults with {{name}} filled in, when no `t` is given. */
const defaultT: TranslateFn = (key, options) => {
  const text: string = typeof options?.defaultValue === 'string' ? options.defaultValue : key;
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
    options && options[name] != null ? String(options[name]) : match,
  );
};

/** The newest point time of some [time, value] lists, or null. */
const newestPoint = (lists: ReadonlyArray<ReadonlyArray<ReadonlyArray<number | null>>>): number | null => {
  let newest: number | null = null;
  lists.forEach((points) => {
    points.forEach((p) => {
      const t = p[0];
      if (typeof t === 'number' && Number.isFinite(t) && (newest === null || t > newest)) newest = t;
    });
  });
  return newest;
};

/** The x-axis window: the range back from the newest point; null (no
 *  window, every reading) for All or without points. */
const windowStart = (latest: number | null, rangeMs: number | null): number | null =>
  latest !== null && rangeMs !== null ? latest - rangeMs : null;

export interface PhoneBaseConfig {
  /** IANA timezone of the pour: the axis and tooltip show its local time. */
  timezoneIana?: string;
  height: number;
}

/** The options every phone chart starts from (see the module comment). */
export const getPhoneTimeSeriesOptions = ({ timezoneIana, height }: PhoneBaseConfig): Highcharts.Options => ({
  chart: {
    height,
    // No zoom at all: Highcharts Stock turns on pinch and wheel zoom by
    // default (with its own English "Reset zoom" button); an undefined
    // pinchType overrides that default.
    zooming: { type: undefined, pinchType: undefined, mouseWheel: { enabled: false } },
    panning: { enabled: false },
    spacing: [8, 4, 8, 4],
    animation: false,
  },
  time: timezoneIana ? { timezone: timezoneIana } : undefined,
  credits: { enabled: false },
  exporting: { enabled: false },
  rangeSelector: { enabled: false },
  navigator: { enabled: false },
  scrollbar: { enabled: false },
  legend: { enabled: false },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    crosshair: true,
    labels: { style: AXIS_LABEL_STYLE },
  },
  yAxis: {
    opposite: false,
    title: { text: undefined },
    labels: { style: AXIS_LABEL_STYLE },
  },
  tooltip: {
    shared: true,
    split: false,
    followTouchMove: true,
    xDateFormat: '%e %b, %H:%M %Z',
    style: { fontSize: '12px' },
  },
  plotOptions: {
    series: {
      findNearestPointBy: 'x',
      stickyTracking: true,
      marker: { enabled: false },
      dataGrouping: { enabled: false },
      animation: false,
    },
    scatter: { findNearestPointBy: 'x' },
  },
});

/** The base y-axis of a phone chart, for building on. */
const baseYAxis = (): Highcharts.YAxisOptions => ({
  opposite: false,
  title: { text: undefined },
  labels: { style: AXIS_LABEL_STYLE },
});

/** Target plotlines (overlayModel's), with phone-sized labels. */
const phoneTargetPlotLines = (lines: OverlayTargetLine[]): Highcharts.YAxisPlotLinesOptions[] =>
  targetPlotLines(lines).map((line) => ({
    ...line,
    label: {
      ...line.label,
      x: -4,
      style: { ...(line.label?.style ?? {}), fontSize: '10px' },
    },
  }));

/** Chart events that hide crowded target labels after every redraw. */
const declutterEvents = (lines: OverlayTargetLine[]): Highcharts.ChartOptions => {
  if (lines.length === 0) return {};
  const priorityById = targetLinePriorities(lines);
  return {
    events: {
      render(this: Highcharts.Chart) {
        declutterTargetLabels(this, priorityById, PHONE_LABEL_GAP);
      },
    },
  };
};

/** Assemble the options from the base, keeping the base's nested parts. */
const assemble = (
  base: Highcharts.Options,
  parts: {
    chart?: Highcharts.ChartOptions;
    xMin: number | null;
    yAxis: Highcharts.YAxisOptions | Highcharts.YAxisOptions[];
    tooltip?: Highcharts.TooltipOptions;
    series: Highcharts.SeriesOptionsType[];
  },
): Highcharts.Options => ({
  ...base,
  chart: { ...base.chart, ...(parts.chart ?? {}) },
  xAxis: { ...(base.xAxis as Highcharts.XAxisOptions), min: parts.xMin },
  yAxis: parts.yAxis,
  tooltip: { ...base.tooltip, ...(parts.tooltip ?? {}) },
  series: parts.series,
});

// ---------------------------------------------------------------------------
// Charts tab: one metric overlaid for every location
// ---------------------------------------------------------------------------

export interface PhoneOverlayOptions {
  /** The pour's limits: their Criteria '02' rows are the temperature
   *  chart's limit lines. */
  limits?: ReadonlyArray<Pour2Limit>;
  /** Evaporation limits (a location chart response's evaLimit) for the
   *  evaporation chart. */
  evaLimits?: ReadonlyArray<ChartLimit> | null;
  /** The selected range's length; null for All. */
  rangeMs: number | null;
  /** The end of the window; the newest point when absent. */
  latest?: number | null;
  /** Series ids hidden with the legend chips. */
  hidden?: ReadonlySet<string>;
  /** Humidity series on a second 0-100 axis (Temp & RH); on whenever the
   *  panel has humidity series unless set. */
  humidityAxis?: boolean;
  timezoneIana?: string;
  height?: number;
  t?: TranslateFn;
}

/**
 * The Charts tab's chart for one overlay panel (overlayModel
 * buildOverlayPanels with one metric) and its metric config (metricConfig):
 * a line per location in the desktop overlay's colours, with the
 * temperature limit lines, the strength / maturity target lines, tested
 * band and achievement markers, and the evaporation limit lines.
 */
export const buildPhoneOverlayOptions = (
  panel: OverlayPanel,
  cfg: MetricConfig,
  {
    limits = [],
    evaLimits,
    rangeMs,
    latest,
    hidden = NOTHING_HIDDEN,
    humidityAxis,
    timezoneIana,
    height = PHONE_OVERLAY_HEIGHT,
    t = defaultT,
  }: PhoneOverlayOptions,
): PhoneChartBuild => {
  const colourOf = (i: number): string => SERIES_COLORS[i % SERIES_COLORS.length];
  const hasHumidity = panel.seriesData.some((s) => s.isHumidity);
  const secondAxis = humidityAxis ?? hasHumidity;
  const fallbackName = t('mobile.charts.limit', { defaultValue: 'Limit' });

  const lines = panel.seriesData.map((s, i): Highcharts.SeriesLineOptions => {
    const colour = colourOf(i);
    const id = s.id ?? `series-${i}`;
    return {
      id,
      type: 'line',
      name: s.isHumidity
        ? t('mobile.charts.humiditySeries', { defaultValue: '{{name}} (RH)', name: s.name })
        : s.name,
      data: s.data,
      color: colour,
      visible: !hidden.has(id),
      findNearestPointBy: 'x',
      marker: { enabled: false },
      ...(s.isHumidity && secondAxis
        ? { yAxis: 1, dashStyle: 'ShortDash' as Highcharts.DashStyleValue, tooltip: { valueSuffix: ' %' } }
        : {}),
      // Strength outside the location's tested span is a trend estimate:
      // dashed and faded, and its tooltip says so.
      ...(panel.metric === 'strength' && s.span
        ? {
            zoneAxis: 'x',
            zones: extrapolatedZones(s.span, colour),
            tooltip: { pointFormatter: strengthPointFormatter(s.span) },
          }
        : {}),
    };
  });
  const markers = panel.seriesData.flatMap((s, i): Highcharts.SeriesScatterOptions[] =>
    s.id && s.markers && s.markers.length > 0
      ? [{ ...markerSeries(s, colourOf(i)), findNearestPointBy: 'x', visible: !hidden.has(s.id) }]
      : [],
  );

  const end = latest ?? newestPoint(panel.seriesData.map((s) => s.data));
  const targetLines = panel.targetLines ?? [];
  const targetValues = targetLines.map((line) => line.value);
  const dataMax = panel.seriesData.reduce(
    (max, s) => s.data.reduce((m, [, v]) => (v > m ? v : m), max),
    -Infinity,
  );

  let yAxis: Highcharts.YAxisOptions | Highcharts.YAxisOptions[];
  if (secondAxis) {
    // Temperature on the left; humidity on a fixed 0-100 axis on the right.
    yAxis = [
      baseYAxis(),
      { ...baseYAxis(), min: 0, max: 100, opposite: true },
    ];
  } else if (panel.metric === 'temperature') {
    const lim = temperatureLimitLines(limits, null, { isSiteHardware: false, labels: 'short', fallbackName });
    yAxis = { ...baseYAxis(), plotLines: lim.plotLines, softMax: lim.softMax };
  } else if (panel.metric === 'weather') {
    const lim = shortLimitLines(
      (evaLimits ?? []).map((l): PhoneLimit => ({ name: l.name ?? null, operator: l.operator, value: l.value, color: null })),
      { unit: '', fallbackName, headroom: EVAPORATION_LABEL_HEADROOM },
    );
    yAxis = { ...baseYAxis(), min: 0, plotLines: lim.plotLines, softMax: lim.softMax };
  } else {
    yAxis = {
      ...baseYAxis(),
      plotLines: phoneTargetPlotLines(targetLines),
      ...(panel.metric === 'strength'
        ? {
            min: 0,
            softMax: strengthAxisSoftMax(panel.strengthRange, targetValues, Number.isFinite(dataMax) ? dataMax : null),
            plotBands: testedRangeBand(panel.strengthRange),
          }
        : { softMax: maturityAxisSoftMax(targetValues) }),
    };
  }

  const base = getPhoneTimeSeriesOptions({ timezoneIana, height });
  return {
    options: assemble(base, {
      chart: declutterEvents(targetLines),
      xMin: windowStart(end, rangeMs),
      yAxis,
      tooltip: { valueDecimals: cfg.valueDecimals, valueSuffix: cfg.valueSuffix },
      series: [...lines, ...markers],
    }),
    legend: lines.map((line, i) => ({
      id: line.id as string,
      label: line.name as string,
      color: colourOf(i),
      // A humidity series reads %RH, whatever the panel's metric.
      value: panel.seriesData[i]?.isHumidity
        ? newestValueText(panel.seriesData[i].data, 0, ' %RH')
        : newestValueText(panel.seriesData[i]?.data ?? [], cfg.valueDecimals, cfg.valueSuffix),
    })),
    hasData: panel.seriesData.some((s) => s.data.length > 0),
    latest: end,
  };
};

/** The legend chips of an overlay panel, without building the chart. */
export const phoneOverlayLegend = (panel: OverlayPanel, t: TranslateFn = defaultT): PhoneLegendItem[] =>
  panel.seriesData.map((s, i) => ({
    id: s.id ?? `series-${i}`,
    label: s.isHumidity
      ? t('mobile.charts.humiditySeries', { defaultValue: '{{name}} (RH)', name: s.name })
      : s.name,
    color: SERIES_COLORS[i % SERIES_COLORS.length],
  }));

// ---------------------------------------------------------------------------
// Location tab: one location, one kind at a time
// ---------------------------------------------------------------------------

/** The Location tab's chart kinds. */
export type PhoneLocationChartKind = 'temp' | 'diff' | 'strength' | 'maturity' | 'eva';

export interface PhoneLocationChartContext {
  /** The selected range's length; null for All. */
  rangeMs: number | null;
  /** The end of the window; the newest point when absent. */
  latest?: number | null;
  /** Series ids hidden with the legend chips. */
  hidden?: ReadonlySet<string>;
  timezoneIana?: string;
  height?: number;
  t?: TranslateFn;
  /** The temperature series' name; the response's location_name else. */
  locationName?: string;
  /** A humidity channel: %RH on a 0-100 axis, no limits. */
  isHumidity?: boolean;
  /** Site-conditions hardware: no temperature limits. */
  isSiteHardware?: boolean;
  /** The location's own temperature limits (dashboard site state); they
   *  win over the pour's limits when sent. */
  tempLimits?: ReadonlyArray<TempLimit> | null;
  /** The pour's limits (Criteria '02' rows), else the response's. */
  pourLimits?: ReadonlyArray<Pour2Limit>;
  /** The location's differentials (dashboard site state): partner names and
   *  the differential limit lines. */
  differentials?: ReadonlyArray<DifferentialNow> | null;
  /** Location id to name, for partners the differentials do not name. */
  partnerNames?: Readonly<Record<string, string>>;
  /** The pour's maturity naming; the response's, else EA, when absent. */
  meta?: MaturityDescriptor;
  /** The company strength unit; the response's when absent. */
  strengthUnit?: string;
}

/** A chart target's name for its line: the purpose (the chart label, else
 *  the older progress bar's name) with its strength, as Location Details. */
const locationTargetName = (chartData: ChartDataResponse, targetId: string, unit: string): string => {
  const chartTarget = (chartData.target ?? []).find((t) => t.target_id === targetId);
  const progressName = (chartData.progress_bar?.targets ?? []).find((t) => t.target_id === targetId)?.target_name;
  return targetDisplayName(
    chartTarget?.target_label?.trim() || targetPurpose(progressName),
    chartTarget?.target_strength,
    unit,
  );
};

/** One location's target lines on its strength or maturity chart. */
const locationTargetLines = (
  chartData: ChartDataResponse,
  metric: 'strength' | 'maturity',
  meta: MaturityDescriptor,
  unit: string,
): OverlayTargetLine[] =>
  (chartData.target ?? [])
    .flatMap((t): OverlayTargetLine[] => {
      const value = metric === 'strength' ? t.target_strength : t.target_ea;
      if (value == null || !(value > 0)) return [];
      const at = metric === 'maturity' ? ` at ${value.toFixed(1)} ${meta.unit}` : '';
      const flag = isTargetOutsideRange(t, chartData.calibrated_range)
        ? `, ${TARGET_OUTSIDE_RANGE_LABEL.toLowerCase()}`
        : '';
      return [{ value, label: `${locationTargetName(chartData, t.target_id, unit)}${at}${flag}`, achieved: !!t.achieved }];
    })
    .sort((a, b) => a.value - b.value);

/** [time, value] points of a list, without empty values. */
const points = (rows: ReadonlyArray<ReadonlyArray<number | null>> | null | undefined, valueIndex = 1): [number, number][] =>
  (rows ?? [])
    .map((p) => [p[0], valueIndex === 2 ? p[2] ?? p[1] : p[valueIndex]])
    .filter((p) => p[0] != null && p[1] != null) as [number, number][];

/**
 * One location's chart of one kind, from its chart response:
 *  - temp: temperature (or %RH) and the tank, with the location's limit
 *    lines (its own when the dashboard sent them, else the pour's);
 *  - diff: one line per differential partner, named "vs <partner>", with
 *    the differential limit lines from the dashboard's differentials;
 *  - strength / maturity: the curve with its target lines, achievement
 *    markers, tested band and trend estimate, as Location Details;
 *  - eva: the evaporation rate with its limit lines.
 */
export const buildPhoneLocationChartOptions = (
  kind: PhoneLocationChartKind,
  chartData: ChartDataResponse,
  ctx: PhoneLocationChartContext,
): PhoneChartBuild => {
  const {
    rangeMs,
    hidden = NOTHING_HIDDEN,
    timezoneIana,
    height = PHONE_LOCATION_CHART_HEIGHT,
    t = defaultT,
  } = ctx;
  const fallbackName = t('mobile.charts.limit', { defaultValue: 'Limit' });
  const base = getPhoneTimeSeriesOptions({ timezoneIana, height });
  const meta = ctx.meta ?? maturityMeta(chartData);
  const unit = resolveStrengthUnit(ctx.strengthUnit, chartData.strength_unit);
  const legend: PhoneLegendItem[] = [];

  /** A line series, listed in the legend. */
  const line = (id: string, name: string, color: string, data: [number, number][], extra: Partial<Highcharts.SeriesLineOptions> = {}): Highcharts.SeriesLineOptions => {
    legend.push({ id, label: name, color });
    return {
      id,
      type: 'line',
      name,
      data,
      color,
      visible: !hidden.has(id),
      findNearestPointBy: 'x',
      marker: { enabled: false },
      ...extra,
    };
  };

  const series: Highcharts.SeriesOptionsType[] = [];
  let yAxis: Highcharts.YAxisOptions = baseYAxis();
  let tooltip: Highcharts.TooltipOptions = { valueDecimals: 1, valueSuffix: ' °C' };
  let chartExtra: Highcharts.ChartOptions = {};

  if (kind === 'temp') {
    const name = ctx.locationName || chartData.location_name
      || (ctx.isHumidity
        ? t('mobile.charts.humidity', { defaultValue: 'Relative humidity' })
        : t('mobile.charts.temperature', { defaultValue: 'Temperature' }));
    series.push(line('temp', name, TEMP_COLOR, points(chartData.temp)));
    const tank = points(chartData.tankTemp);
    if (tank.length > 0) {
      series.push(line('tank', t('mobile.charts.tankSeries', { defaultValue: '{{name}} Tank', name }), TANK_COLOR, tank));
    }
    if (ctx.isHumidity) {
      // RH is bounded 0-100 and has no limits.
      yAxis = { ...yAxis, min: 0, max: 100 };
      tooltip = { valueDecimals: 1, valueSuffix: ' %' };
    } else {
      const lim = ctx.isSiteHardware
        ? { plotLines: [], softMax: undefined }
        : ctx.tempLimits != null
          ? shortLimitLines(ctx.tempLimits, { fallbackName })
          : temperatureLimitLines(ctx.pourLimits ?? [], chartData.limit, { isSiteHardware: false, labels: 'short', fallbackName });
      yAxis = { ...yAxis, min: 0, plotLines: lim.plotLines, softMax: lim.softMax };
    }
  } else if (kind === 'diff') {
    const partnerName = (id: string): string =>
      ctx.differentials?.find((d) => d.partner_id === id)?.partner_name
      || ctx.partnerNames?.[id]
      || (chartData.temp_differential_locations ?? []).find((d) => d.location_id === id)?.location_name
      || id;
    Object.entries(chartData.difftemp ?? {}).forEach(([partnerId, data], i) => {
      series.push(line(
        `diff-${partnerId}`,
        t('mobile.charts.vsPartner', { defaultValue: 'vs {{name}}', name: partnerName(partnerId) }),
        SERIES_COLORS[i % SERIES_COLORS.length],
        points(data),
      ));
    });
    // The dashboard's differentials carry the configured limits; the chart
    // response's diffLimit is the fallback.
    const seen = new Set<string>();
    const limits: PhoneLimit[] = [];
    (ctx.differentials ?? []).forEach((d) => {
      if (d.limit_value == null) return;
      const operator = d.operator || '>=';
      const id = `${operator}|${d.limit_value}`;
      if (seen.has(id)) return;
      seen.add(id);
      limits.push({ name: d.limit_name, operator, value: d.limit_value, color: null });
    });
    if (limits.length === 0) {
      (chartData.diffLimit ?? []).forEach((l) => {
        limits.push({ name: l.name ?? null, operator: l.operator, value: l.value, color: null });
      });
    }
    const lim = shortLimitLines(limits, { fallbackName });
    yAxis = { ...yAxis, plotLines: lim.plotLines, softMax: lim.softMax };
  } else if (kind === 'strength' || kind === 'maturity') {
    const strength = kind === 'strength';
    const data = strength ? points(chartData.ea) : points(chartData.ea, 2);
    const range = chartData.calibrated_range ?? null;
    const targetLines = locationTargetLines(chartData, kind, meta, unit);
    const span = strength ? testedTimeSpan(chartData.ea, range) : null;
    const name = strength
      ? t('mobile.charts.strengthSeries', { defaultValue: 'Estimated strength' })
      : meta.index_label;
    const colour = strength ? STRENGTH_COLOR : MATURITY_COLOR;
    series.push(line(kind, name, colour, data, strength && span
      ? {
          zoneAxis: 'x',
          zones: extrapolatedZones(span, colour),
          tooltip: { pointFormatter: strengthPointFormatter(span) },
        }
      : {}));
    const markers = achievementPoints(chartData.target, data, {
      chart: kind,
      strengthUnit: unit,
      meta,
      range,
      concreteStart: chartData.location_details?.ea_started ?? chartData.ea_start,
      monitoringStart: chartData.location_details?.logging_started,
    });
    if (markers.length > 0) {
      series.push({
        ...markerSeries({ id: kind, name, data, markers }, REACHED_MARKER_COLOR),
        findNearestPointBy: 'x',
        visible: !hidden.has(kind),
      });
    }
    const dataMax = data.reduce<number | null>((m, [, v]) => (m === null || v > m ? v : m), null);
    yAxis = {
      ...yAxis,
      min: 0,
      plotLines: phoneTargetPlotLines(targetLines),
      ...(strength
        ? {
            softMax: strengthAxisSoftMax(range, (chartData.target ?? []).map((tg) => tg.target_strength), dataMax),
            plotBands: testedRangeBand(range),
          }
        : { softMax: maturityAxisSoftMax((chartData.target ?? []).map((tg) => tg.target_ea)) }),
    };
    tooltip = { valueDecimals: 1, valueSuffix: ` ${strength ? unit : meta.unit}` };
    chartExtra = declutterEvents(targetLines);
  } else {
    series.push(line('eva', t('mobile.charts.evaporationSeries', { defaultValue: 'Evaporation rate' }), EVAPORATION_COLOR, points(chartData.eva)));
    const lim = shortLimitLines(
      (chartData.evaLimit ?? []).map((l): PhoneLimit => ({ name: l.name ?? null, operator: l.operator, value: l.value, color: null })),
      { unit: '', fallbackName, headroom: EVAPORATION_LABEL_HEADROOM },
    );
    yAxis = { ...yAxis, min: 0, plotLines: lim.plotLines, softMax: lim.softMax };
    tooltip = { valueDecimals: 2, valueSuffix: ' kg/m²/h' };
  }

  const lines = series.filter((s): s is Highcharts.SeriesLineOptions => s.type === 'line');
  const end = ctx.latest ?? newestPoint(lines.map((s) => (s.data ?? []) as [number, number][]));
  return {
    options: assemble(base, {
      chart: chartExtra,
      xMin: windowStart(end, rangeMs),
      yAxis,
      tooltip,
      series,
    }),
    legend,
    hasData: lines.some((s) => (s.data ?? []).length > 0),
    latest: end,
  };
};
